import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action} from '@ngrx/store';
import {Observable} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import {
  CreateWikiGroupAction,
  CreateWikiGroupSuccessAction,
  CreateWikiItemAction,
  CreateWikiItemSuccessAction,
  DeleteWikiGroupAction,
  DeleteWikiGroupSuccessAction,
  DeleteWikiItemAction,
  DeleteWikiItemSuccessAction,
  GetAllWikiGroupsAction,
  GetAllWikiGroupsSuccessAction,
  GetWikiItemAction,
  GetWikiItemSuccessAction,
  UpdateWikiGroupAction,
  UpdateWikiGroupsOrderAction,
  UpdateWikiGroupsOrderSuccessAction,
  UpdateWikiGroupSuccessAction,
  UpdateWikiItemAction,
  UpdateWikiItemOrderAction,
  UpdateWikiItemSuccessAction,
  WikiActionType,
} from './wiki.action';
import {createCallbackActions, emitErrorActions} from '../store.utils';
import {WikiApiService} from '../../api/wiki-api.service';

@Injectable()
export class WikiEffects {
  constructor(
    private actions$: Actions,
    private wikiApiService: WikiApiService
  ) {}

  public getAll$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllWikiGroupsAction>(WikiActionType.GET_ALL),
      mergeMap(action =>
        this.wikiApiService.getAll().pipe(
          mergeMap(wikiList => [
            new GetAllWikiGroupsSuccessAction({wikiList}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateWikiGroupAction>(WikiActionType.CREATE_WIKI_GROUP),
      mergeMap(action =>
        this.wikiApiService.createWikiGroup(action.payload.wikiGroup).pipe(
          mergeMap(wikiGroup => [
            new CreateWikiGroupSuccessAction({wikiGroup: wikiGroup}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateWikiGroupAction>(WikiActionType.UPDATE_WIKI_GROUP),
      mergeMap(action =>
        this.wikiApiService.updateWikiGroup(action.payload.wikiGroup).pipe(
          mergeMap(wikiGroup => [
            new UpdateWikiGroupSuccessAction({wikiGroup}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public delete$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteWikiGroupAction>(WikiActionType.DELETE_WIKI_GROUP),
      mergeMap(action =>
        this.wikiApiService.deleteWikiGroup(action.payload.id).pipe(
          mergeMap(() => [
            new DeleteWikiGroupSuccessAction({id: action.payload.id}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public createItem$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateWikiItemAction>(WikiActionType.CREATE_WIKI_ITEM),
      mergeMap(action =>
        this.wikiApiService.createWikiItem(action.payload.wikiItem).pipe(
          mergeMap(wikiItem => [
            new CreateWikiItemSuccessAction({wikiItem}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public updateItem$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateWikiItemAction>(WikiActionType.UPDATE_WIKI_ITEM),
      mergeMap(action =>
        this.wikiApiService.updateWikiItem(action.payload.wikiItem).pipe(
          mergeMap(wikiItem => [
            new UpdateWikiItemSuccessAction({wikiItem}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public deleteItem$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteWikiItemAction>(WikiActionType.DELETE_WIKI_ITEM),
      mergeMap(action =>
        this.wikiApiService.deleteWikiItem(action.payload.wikiItem).pipe(
          mergeMap(() => [
            new DeleteWikiItemSuccessAction({id: action.payload.wikiItem._id}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public updateGroupOrder$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateWikiGroupsOrderAction>(WikiActionType.UPDATE_WIKI_GROUP_ORDER),
      mergeMap(action =>
        this.wikiApiService.updateWikiGroupOrder(action.payload.wikiGroupOrder).pipe(
          mergeMap(payload => [
            new UpdateWikiGroupsOrderSuccessAction(payload.message),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public updateItemOrder$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateWikiItemOrderAction>(WikiActionType.UPDATE_WIKI_ITEM_ORDER),
      mergeMap(action =>
        this.wikiApiService.updateWikiItemOrder(action.payload.wikiItemOrder).pipe(
          mergeMap(payload => [
            new UpdateWikiGroupsOrderSuccessAction(payload.message),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );

  public getWikiItem$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetWikiItemAction>(WikiActionType.GET_WIKI_ITEM),
      mergeMap(action =>
        this.wikiApiService.getWikiItem(action.payload.itemId).pipe(
          mergeMap(wikiItem => [
            new GetWikiItemSuccessAction({wikiItem}),
            ...createCallbackActions(action.payload.onSuccess),
          ]),
          catchError(error => emitErrorActions(error, action.payload.onFailure))
        )
      )
    )
  );
}
